/* SpawnAnything — multiplayer controller on the main thread (lockstep, see docs/multiplayer.md).
   Owns the relay connection (createLockstepClient) and the lobby UI (src/ui/lobby.js); main.js only provides a small
   adapter (`api`) and routes possession, input, tools, upgrades and speed through here while a match runs.

   api = {
     worker,                          the sim worker (turns are forwarded to it)
     toast(msg),
     hostSetup(seed) -> object,       host: the battle setup to broadcast (src/net/mpSetup.js helpers)
     startMatch(setup, info) -> gen,  every client (host included) builds the battle from the received setup
     endMatch(),                      back to the single-player sandbox
     possessed(i),                    the sim confirms that the local player steers unit i (after a resume)
     possessRejected(),               the sim did not accept the possession (unit taken, dead or enemy)
   } */
import { createLockstepClient, defaultRelayUrl } from './lockstep.js';
import { createLobbyUI } from '../ui/lobby.js';
import { MP_SETUP_VERSION } from './mpSetup.js';

const SESSION_KEY = 'aw-mp-session';
const QPOS = 1 << 20;
const quant = (v, s, lim) => { const r = Math.round((+v || 0) * s); return r > lim ? lim : r < -lim ? -lim : r; };
const now = () => performance.now();

export function createMultiplayer(api) {
  let net = null, name = 'Spieler';
  let match = null;                          // { players, hostId, me, team, turnMs, gen }
  let lobbyPlayers = [];
  let lastTick = 0, stalledAt = 0, lastStat = 0, ts = 1, stallMs = 0, lastFrameT = 0;
  // input coalescing: latest state per turn, attack/jump/special are OR-ed between two submissions
  let inp = null, sentIn = '', forceIn = false, atkSeen = false, jump = false, special = false;
  let wantPos = -1, wantAt = 0;

  const ui = createLobbyUI({
    onCreate: n => connectAnd(n, c => c.createRoom()),
    onJoin: (code, n) => connectAnd(n, c => c.joinRoom(code)),
    onTeam: t => net && net.setTeam(t),
    onReady: b => net && net.setReady(b),
    onStart: hostStart,
    onLeave: leave,
  });

  /* ---------------- connection ---------------- */
  function fresh(n) {
    dispose();
    name = n || 'Spieler';
    const c = net = createLockstepClient({ url: defaultRelayUrl(), name });
    c.on('welcome', saveSession);
    c.on('lobby', onLobby);
    c.on('start', onStart);
    c.on('error', e => { if (c !== net) return; if (match) api.toast(e.message); else ui.error(e.message); });
    c.on('desync', d => {
      console.warn('Mehrspieler: Desync', d);
      if (!match || match.desync) return; // keep the first divergent turn on screen
      match.desync = d.turn;
      ui.desync(`Spielstand weicht ab (Zug ${d.turn}): die Simulation ist nicht mehr synchron. Bitte das Spiel neu starten.`);
    });
    c.on('reconnected', () => api.toast('Wieder verbunden'));
    c.on('close', e => {
      if (c !== net || e.reason === 'left') return;
      const was = !!match;
      leave();
      if (was) api.toast('Die Verbindung zum Server ist verloren. Zurück im Sandkasten.');
      else ui.error('Die Verbindung zum Server ist verloren.');
    });
    c.on('submit', onSubmit);
    c.onTurn(t => { if (match && c === net) api.worker.postMessage({ cmd: 'turn', gen: match.gen, turn: t.turn, cmds: t.cmds, d: c.inputDelay }); });
    return c;
  }
  function dispose() {
    const c = net; net = null;
    if (c) { try { c.close(); } catch (e) { } }
  }
  async function connectAnd(n, fn) {
    if (net && net.code) return;
    const c = fresh(n);
    ui.busy(true);
    try { await fn(c); } catch (e) { if (c === net) { ui.error(e.message || 'Verbindung zum Server fehlgeschlagen.'); dispose(); } }
    finally { ui.busy(false); }
  }
  function saveSession() {
    try { if (net && net.session) sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...net.session, name })); } catch (e) { }
  }
  function clearSession() { try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { } }

  function onLobby(l) {
    saveSession();
    if (match) {
      // seats that vanished mid-match (left or reconnect window expired); the host role moves like in the sim (mpLeft):
      // to the lowest player id still in the match
      const gone = lobbyPlayers.filter(p => !l.players.some(q => q.id === p.id));
      if (gone.length && !l.players.some(q => q.id === match.hostId)) {
        const h = l.players.reduce((m, q) => (!m || q.id < m ? q.id : m), 0);
        if (h) match.hostId = h;
        const nh = l.players.find(q => q.id === h);
        api.toast(`${gone.map(p => p.name).join(', ')} hat das Spiel verlassen. ${h === l.you ? 'Du bist jetzt Gastgeber.' : nh ? nh.name + ' ist jetzt Gastgeber.' : ''}`);
      } else if (gone.length) api.toast(`${gone.map(p => p.name).join(', ')} hat das Spiel verlassen.`);
    }
    lobbyPlayers = l.players;
    // during a match the host is the one who started it (only he may pause), even if the relay migrated the lobby host
    ui.set({ phase: 'room', code: l.code, players: l.players, you: l.you, isHost: match ? match.hostId === l.you : l.isHost, running: !!match });
  }

  /* ---------------- match start ---------------- */
  function hostStart() {
    if (!net || !net.isHost) return;
    const seed = ((Math.random() * 0xfffffffe) >>> 0) + 1;
    let setup;
    try { setup = api.hostSetup(seed); } catch (e) { console.error(e); ui.error('Die Aufstellung konnte nicht übertragen werden.'); return; }
    setup.v = MP_SETUP_VERSION; setup.hostId = net.playerId;
    const bytes = JSON.stringify(setup).length;
    if (bytes > 1_450_000) { ui.error('Die Aufstellung ist zu groß für das Netz (zu viele eingezeichnete Einheiten).'); return; }
    net.start({ seed, setup });
  }

  function onStart(s) {
    const S = s.setup;
    if (!S || S.v !== MP_SETUP_VERSION) { ui.error('Client und Gastgeber passen nicht zusammen. Bitte Seite neu laden.'); return; }
    const players = (s.players || []).slice().sort((a, b) => a.id - b.id);
    const me = players.findIndex(p => p.id === s.you);
    match = { players, hostId: S.hostId, me, team: me >= 0 ? players[me].team : 0, turnMs: s.turnMs || 100, gen: 0 };
    lastTick = 0; stalledAt = 0; ts = 1; stallMs = 0; lastFrameT = 0;
    inp = null; sentIn = ''; forceIn = false; atkSeen = jump = special = false; wantPos = -1; wantAt = 0;
    ui.set({ running: true, isHost: S.hostId === s.you }); ui.close(); ui.desync('');
    const info = { seed: s.seed, mp: { tpt: s.ticksPerTurn, players: players.map(p => ({ id: p.id, team: p.team })), host: S.hostId, me: Math.max(0, me), tools: S.tools } };
    try { match.gen = api.startMatch(S, info); }
    catch (e) { console.error(e); api.toast('Die Aufstellung des Gastgebers konnte nicht geladen werden.'); leave(); }
  }

  function leave() {
    const was = !!match;
    match = null; clearSession(); dispose();
    ui.set({ phase: 'menu', running: false, players: [], code: '', isHost: false }); ui.waiting(''); ui.desync('');
    if (was) api.endMatch();
  }

  /* ---------------- per turn: coalesced input ---------------- */
  function onSubmit() {
    if (!match || !inp || wantPos < 0) return;
    const c = { t: 'in', mx: quant(inp.mx, 127, 255), mz: quant(inp.mz, 127, 255), ax: quant(inp.ax, 16, QPOS), az: quant(inp.az, 16, QPOS),
      f: (atkSeen || inp.attack ? 1 : 0) | (inp.sprint ? 2 : 0) | (inp.auto ? 4 : 0) };
    if (jump) c.j = 1;
    if (special) c.s = 1;
    const key = c.mx + ',' + c.mz + ',' + c.ax + ',' + c.az + ',' + c.f;
    if (forceIn || jump || special || key !== sentIn) { net.queueCommand(c); sentIn = key; forceIn = false; }
    jump = special = false; atkSeen = false;
  }

  /* ---------------- worker / frame loop hooks ---------------- */
  function onFrame(m) {
    if (!match || !m.mp || !net) return;
    lastTick = m.mp.tick; ts = m.mp.ts;
    submit(); // also from here: rAF stops in background tabs, worker messages do not
    const P = m.P;
    if (wantPos >= 0 && wantAt) {
      if (P && P.i === wantPos) wantAt = 0;
      else if (now() - wantAt > 1500 + net.inputDelay * match.turnMs * 2) { wantAt = 0; wantPos = -1; api.possessRejected(); }
    } else if (P && P.alive && P.i !== wantPos && !wantAt && net.bufferedTurns(lastTick) <= net.inputDelay + 1) {
      wantPos = P.i; forceIn = true; api.possessed(P.i); // e.g. after reloading the page mid-match
    }
  }
  // Report progress one turn ahead: the client then submits turn T+d while the worker is still in T-1, so with d = 1
  // the turn arrives before the worker needs it instead of costing one round trip of stall every turn.
  function submit() {
    net.canAdvance(lastTick + net.ticksPerTurn);
    return net.turn >= Math.floor(lastTick / net.ticksPerTurn); // turn of the worker's next tick delivered?
  }
  function tick(t) {
    if (!match || !net) return;
    const ok = submit();
    if (ok) stalledAt = 0; else if (!stalledAt) stalledAt = t;
    if (!ok && lastFrameT) stallMs += Math.min(100, t - lastFrameT);
    lastFrameT = t;
    const behind = net.bufferedTurns(lastTick) - net.inputDelay;
    ui.waiting(net.status === 'reconnecting' ? 'Verbindung unterbrochen – verbinde neu …'
      : stalledAt && t - stalledAt > 300 ? 'Warte auf Mitspieler …'
        : behind > 15 ? 'Hole den Spielstand auf …'
          : ts === 0 ? 'Pausiert' : '');
    if (t - lastStat > 500) {
      lastStat = t;
      const names = [[], []];
      for (const p of match.players) {
        const lp = lobbyPlayers.find(q => q.id === p.id);
        names[p.team].push((lp && !lp.connected ? '(' + p.name + ')' : p.name) + (p.id === net.playerId ? '*' : ''));
      }
      ui.status({ rtt: net.rtt, delay: net.inputDelay, teams: names });
    }
  }

  /* ---------------- commands ---------------- */
  const cmd = c => { if (match && net) net.queueCommand(c); };

  // resume a stored session (page reload within 15 s keeps the seat)
  try {
    const s = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
    if (s && s.code && s.token) {
      const c = fresh(s.name);
      c.resume(s).catch(() => { if (c === net) { clearSession(); dispose(); ui.set({ phase: 'menu' }); } });
    }
  } catch (e) { }

  let hashN = 0, lastHash = null; const hashLog = [];
  // dev console helper: __awMP.net.stats, __awMP.hashes (never in production builds)
  if (import.meta.env && import.meta.env.DEV) globalThis.__awMP = { get net() { return net; }, get match() { return match; }, get hashes() { return { n: hashN, last: lastHash, log: hashLog.slice() }; }, get stallMs() { return stallMs; } };

  return {
    get active() { return !!match; },
    get team() { return match ? match.team : 0; },
    get isHost() { return !!(match && net && match.hostId === net.playerId); },
    onFrame,
    onHash(m) { if (match && net) { net.reportHash(m.turn, m.hash); hashN++; lastHash = { turn: m.turn, hash: m.hash }; hashLog.push(m.turn + ':' + m.hash); if (hashLog.length > 200) hashLog.shift(); } },
    tick,
    /** possess unit i (-1 = god mode); the sim checks team and that nobody else steers it */
    possess(i) {
      if (!match || i === wantPos) return;
      wantPos = i; wantAt = i >= 0 ? now() : 0; forceIn = true;
      cmd({ t: 'pos', i });
    },
    /** latest possessed-unit input of this frame: { mx, mz, ax, az, attack, sprint, auto, jump, special } */
    input(o) {
      inp = o; if (o.attack) atkSeen = true; if (o.jump) jump = true; if (o.special) special = true;
    },
    bomb(k, x, z) { cmd({ t: 'bomb', k, x: quant(x, 16, QPOS), z: quant(z, 16, QPOS) }); },
    /** feature command (plain JSON, integers; the sim validates it per player and team, see featCmd in sim.worker.js) */
    send(c) { if (c && typeof c === 'object' && typeof c.t === 'string') cmd(c); },
    /** returns false (and explains) when the building belongs to the other team */
    upgrade(id, team) {
      if (match && team !== match.team) { api.toast('Du kannst nur Gebäude deines Teams ausbauen.'); return false; }
      cmd({ t: 'upg', id }); return true;
    },
    /** host only: 0 = pause, 0.25, 1, 2 */
    speed(v) {
      if (!this.isHost) { api.toast('Nur der Gastgeber kann pausieren oder die Zeit ändern.'); return; }
      cmd({ t: 'speed', v });
    },
    get timeScale() { return ts; },
    leave,
  };
}
